import React from 'react';
import { Card, CardContent, Typography, Box, Divider } from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useTheme } from '@mui/material/styles';

const PerformanceCard = ({ title, value, unit, icon, color, valueColor }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    
    return (
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" flex={1}>
            <Box sx={{ 
                p: 1.5, 
                borderRadius: '50%', 
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', 
                color: color,
                mb: 2 
            }}>
                {icon}
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666', mb: 0.5, fontSize: '0.75rem' }}>{title}</Typography>
            <Box display="flex" alignItems="baseline" gap={0.5}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: valueColor || (isDark ? '#fff' : '#000') }}>{value}</Typography>
                {unit && <Typography variant="caption" sx={{ color: isDark ? '#666' : '#999', fontSize: '0.65rem' }}>{unit}</Typography>}
            </Box>
        </Box>
    );
};

const MilestoneNode = ({ stage, status, daysDone, active, completed }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    
    const getIcon = () => {
        if (stage === 1) return <LocalFireDepartmentIcon sx={{ fontSize: 28 }} />;
        if (stage === 2) return <EmojiEventsIcon sx={{ fontSize: 28 }} />;
        if (stage === 3) return <WorkspacePremiumIcon sx={{ fontSize: 28 }} />;
        return <WorkspacePremiumIcon sx={{ fontSize: 32, filter: completed ? 'drop-shadow(0 0 10px #ffd700)' : 'none' }} />;
    };

    const iconColor = active || completed 
        ? (stage === 1 ? '#00e676' : stage === 2 ? '#ffa726' : stage === 3 ? '#ff7043' : '#ffd700')
        : (isDark ? '#333' : '#e0e0e0');

    return (
        <Box display="flex" flexDirection="column" alignItems="center" sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ 
                mb: 1,
                color: iconColor,
                filter: active ? `drop-shadow(0 0 12px ${iconColor}cc)` : 'none',
                transform: active ? 'scale(1.2)' : 'scale(1)',
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                display: 'flex',
                justifyContent: 'center',
                animation: active ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                    '0%': { transform: 'scale(1.2)' },
                    '50%': { transform: 'scale(1.3)' },
                    '100%': { transform: 'scale(1.2)' },
                }
            }}>
                {getIcon()}
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#888' : '#666', fontSize: '0.75rem', fontWeight: active ? 'bold' : 'normal' }}>
                Stage {stage}
            </Typography>
            <Typography variant="caption" sx={{ color: active || completed ? iconColor : (isDark ? '#444' : '#bbb'), fontSize: '0.65rem', fontWeight: active ? 'bold' : 'normal' }}>
                {status}
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#555' : '#aaa', fontSize: '0.6rem', mt: 0.5 }}>
                {daysDone}/7 days
            </Typography>
        </Box>
    );
};

const PerformanceOverview = ({ metrics }) => {
    const { caloriesBurnt, activityScore, streak, bestStreak, currentDay } = metrics || {};
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const currentDayValue = currentDay || 1;
    
    const stages = [
        { stage: 1, title: 'Initiator', threshold: 7 },
        { stage: 2, title: 'Consistent', threshold: 14 },
        { stage: 3, title: 'Disciplined', threshold: 21 },
        { stage: 4, title: 'Champion', threshold: 28 }
    ];

    const currentStage = Math.min(4, Math.floor((currentDayValue - 1) / 7) + 1);
    const isChampion = currentDayValue >= 28;

    const getDaysInStage = (stageIdx) => {
        const threshold = stages[stageIdx].threshold;
        const prevThreshold = stageIdx === 0 ? 0 : stages[stageIdx-1].threshold;
        
        if (currentDayValue >= threshold) return 7;
        if (currentDayValue <= prevThreshold) return 0;
        return currentDayValue - prevThreshold;
    };

    const getMessage = () => {
        if (currentDayValue < 7) return "Starting the journey! Show up this week to earn the Initiator title.";
        if (currentDayValue < 14) return "Great start! Keep the momentum going for Stage 2!";
        if (currentDayValue < 21) return "Solid Stability! Stay consistent to keep progressing.";
        if (currentDayValue < 28) return "Almost a Champion! Just a few more days of discipline.";
        return "Pure Discipline! You've built the habit — now own the lifestyle.";
    };

    const overallProgress = Math.min((currentDayValue / 28) * 100, 100);

    return (
        <Card sx={{ 
            backgroundColor: isDark ? '#1e1e1e' : '#fff', 
            color: isDark ? '#fff' : '#000', 
            borderRadius: '10px', // Matching app's new borderRadius
            border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)',
            boxShadow: isDark ? 'none' : '0 4px 20px rgba(0,0,0,0.05)',
            overflow: 'hidden'
        }}>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 3, fontSize: '0.85rem', color: isDark ? '#eee' : '#333' }}>
                    {isChampion ? "YOUR CONSISTENCY" : "CONSISTENCY AWARD"}
                </Typography>

                <Box sx={{ 
                    p: isChampion ? 3 : 2.5, 
                    borderRadius: '10px', 
                    backgroundColor: isDark ? 'rgba(30, 30, 30, 0.6)' : '#fafafa', // Matching app's translucent dark bg
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.03)' : '1px solid #f0f0f0',
                    mb: 3
                }}>
                    {!isChampion ? (
                        <>
                            {/* Progress Line Section */}
                            <Box sx={{ position: 'relative', mt: 3, mb: 5, px: 2 }}>
                                <Box sx={{ 
                                    position: 'absolute', 
                                    top: 14, 
                                    left: '10%', 
                                    right: '10%', 
                                    height: 4, 
                                    backgroundColor: isDark ? '#333' : '#e0e0e0',
                                    borderRadius: 2,
                                    zIndex: 1
                                }}>
                                    <Box sx={{ 
                                        height: '100%', 
                                        width: `${overallProgress}%`, 
                                        backgroundColor: '#3c50b4',
                                        borderRadius: 2,
                                        transition: 'width 0.8s ease-in-out',
                                        boxShadow: '0 0 10px rgba(92, 107, 192, 0.4)'
                                    }} />
                                </Box>

                                <Box display="flex" justifyContent="space-between" sx={{ position: 'relative', zIndex: 2 }}>
                                    {stages.map((s, idx) => (
                                        <MilestoneNode 
                                            key={s.stage}
                                            stage={s.stage}
                                            status={s.title}
                                            daysDone={getDaysInStage(idx)}
                                            active={currentStage === s.stage}
                                            completed={currentDayValue >= s.threshold}
                                        />
                                    ))}
                                </Box>
                            </Box>
                            <Typography variant="body2" sx={{ textAlign: 'center', mb: 1, color: isDark ? '#888' : '#555', fontSize: '0.85rem' }}>
                                {getMessage()}
                            </Typography>
                        </>
                    ) : (
                        <Box>
                            <Box display="flex" justifyContent="center" alignItems="center" gap={6} sx={{ mb: 3 }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{ fontSize: '2rem' }}>🔥</Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: isDark ? '#666' : '#999', display: 'block', fontSize: '0.7rem' }}>Active Days</Typography>
                                        <Box display="flex" alignItems="baseline" gap={0.5}>
                                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#ff9800' }}>{currentDayValue || 0}</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ color: isDark ? '#fff' : '#000' }}>days</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Divider orientation="vertical" flexItem sx={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#eee' }} />
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{ fontSize: '2rem' }}>🏆</Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: isDark ? '#666' : '#999', display: 'block', fontSize: '0.7rem' }}>Best Streak</Typography>
                                        <Box display="flex" alignItems="baseline" gap={0.5}>
                                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#ffd700' }}>{bestStreak || 0}</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ color: isDark ? '#fff' : '#000' }}>days</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                            <Typography variant="body2" sx={{ textAlign: 'center', color: isDark ? '#888' : '#555', fontSize: '0.85rem', fontWeight: 500 }}>
                                {getMessage()}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Performance Metrics Section */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, fontSize: '0.8rem', color: isDark ? '#888' : '#999', textTransform: 'uppercase', letterSpacing: 1 }}>
                    PERFORMANCE METRICS
                </Typography>

                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ 
                    p: 2, 
                    borderRadius: '12px', 
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.01)' : 'transparent' 
                }}>
                    <PerformanceCard 
                        title="Activity Score" 
                        value={`${activityScore || 0}/100`} 
                        unit="%" 
                        icon={<WorkspacePremiumIcon sx={{ fontSize: 24 }} />} 
                        color="#4db6ac"
                        valueColor="#4db6ac"
                    />
                    <Divider orientation="vertical" flexItem sx={{ mx: 2, borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#eee' }} />
                    <PerformanceCard 
                        title="Calories Burnt" 
                        value={caloriesBurnt || 0} 
                        unit="kcal" 
                        icon={<LocalFireDepartmentIcon sx={{ fontSize: 24 }} />} 
                        color="#ff7043"
                        valueColor="#ff7043"
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export default PerformanceOverview;
